/**
 * API Client consolidé basé sur FetchApi
 * Ce fichier remplace api.ts et api/client.ts
 */

import { FetchApi, API_URL } from '../service';

/**
 * Response type générique pour les appels API
 */
export interface ApiResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
}

/**
 * Classe ApiClient pour fournir une interface compatible avec axios
 * tout en utilisant FetchApi sous le capot
 */
class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  /**
   * GET request
   */
  async get<T = any>(url: string, config?: any): Promise<ApiResponse<T>> {
    const fullUrl = url.startsWith('http') ? url : `${this.baseURL}${url}`;
    const data = await FetchApi.get<T>(fullUrl);

    return {
      data,
      status: 200,
      statusText: 'OK',
    };
  }

  /**
   * POST request
   */
  async post<T = any>(url: string, payload?: any, config?: any): Promise<ApiResponse<T>> {
    const fullUrl = url.startsWith('http') ? url : `${this.baseURL}${url}`;

    // Si le payload contient des fichiers, utiliser postFile
    if (payload instanceof FormData || this.hasFiles(payload)) {
      const data = await FetchApi.postFile<T>(fullUrl, payload);
      return {
        data,
        status: 201,
        statusText: 'Created',
      };
    }

    const data = await FetchApi.post<T>(fullUrl, payload);
    return {
      data,
      status: 201,
      statusText: 'Created',
    };
  }

  /**
   * PUT request
   */
  async put<T = any>(url: string, payload?: any, config?: any): Promise<ApiResponse<T>> {
    const fullUrl = url.startsWith('http') ? url : `${this.baseURL}${url}`;

    // Si le payload contient des fichiers, utiliser putFile
    if (payload instanceof FormData || this.hasFiles(payload)) {
      const data = await FetchApi.putFile<T>(fullUrl, payload);
      return {
        data,
        status: 200,
        statusText: 'OK',
      };
    }

    const data = await FetchApi.put<T>(fullUrl, payload);
    return {
      data,
      status: 200,
      statusText: 'OK',
    };
  }

  /**
   * PATCH request (simulé avec PUT)
   */
  async patch<T = any>(url: string, payload?: any, config?: any): Promise<ApiResponse<T>> {
    return this.put<T>(url, payload, config);
  }

  /**
   * DELETE request
   */
  async delete<T = any>(url: string, config?: any): Promise<ApiResponse<T>> {
    const fullUrl = url.startsWith('http') ? url : `${this.baseURL}${url}`;
    const data = await FetchApi.delete<T>(fullUrl);

    return {
      data,
      status: 204,
      statusText: 'No Content',
    };
  }

  /**
   * Vérifier si un objet contient des fichiers
   */
  private hasFiles(obj: any): boolean {
    if (!obj || typeof obj !== 'object') return false;

    return Object.values(obj).some(value => {
      if (value instanceof File || value instanceof Blob) return true;
      if (Array.isArray(value)) {
        return value.some(item => item instanceof File || item instanceof Blob);
      }
      return false;
    });
  }
}

/**
 * Instance du client API
 * Compatible avec l'ancienne interface axios
 */
export const apiClient = new ApiClient(API_URL);

/**
 * Export par défaut pour compatibilité
 */
export default apiClient;
